import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { BrandHeader, BrandFooter, useBrand, getBrand, brandHome } from "@/components/customer/Brand";
import PaymentMethods from "@/components/customer/PaymentMethods";
import { Skeleton } from "@/components/ui/skeleton";
import { http, idr, fmtDate, errMsg, PAY_LABEL, STATUS_LABEL } from "@/lib/api";
import { payBooking } from "@/lib/pay";
import { useT, useLoc } from "@/lib/i18n";

const Hero = ({ b }) => {
  const t = useT();
  const paid = ["paid", "paid_at_channel", "pay_at_hotel"].includes(b.payment_status);
  const bad = b.status === "cancelled" || ["failed", "expired"].includes(b.payment_status);
  const Icon = paid ? CheckCircle2 : bad ? XCircle : Clock;
  const color = paid ? "bg-[#00A698]" : bad ? "bg-[#E1231A]" : "bg-[#FFB700]";
  const title = b.status === "cancelled" ? "Booking dibatalkan" : paid ? "Booking dikonfirmasi!" : bad ? "Pembayaran tidak berhasil" : "Menunggu pembayaran";
  return (
    <div className={`${color} rounded-2xl p-6 text-white`} data-testid="booking-status-hero">
      <Icon className="h-10 w-10" />
      <h1 className="mt-3 font-heading text-3xl font-extrabold">{t(title)}</h1>
      <p className="mt-1 text-white/90">{t("Kode booking:")} <b data-testid="booking-code" className="font-mono-x text-lg">{b.code}</b></p>
    </div>
  );
};

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 text-sm"><span className="text-slate-500">{k}</span><span data-testid={testid} className="text-right font-medium text-slate-900">{v}</span></div>
);

export default function BookingPage() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const nav = useNavigate();
  const t = useT();
  const loc = useLoc();
  const [b, setB] = useState(null);
  const brand = useBrand(b?.property_id || getBrand(), !!b?.branded);
  const [error, setError] = useState("");
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => http.get(`/public/bookings/${code}?email=${encodeURIComponent(email)}`)
    .then((r) => { setB(r.data); return r.data; }).catch((e) => setError(errMsg(e))), [code, email]);

  useEffect(() => {
    let n = 0;
    let timer;
    const tick = async () => {
      const r = await http.post("/payments/refresh", { code, email }).catch(() => null);
      const cur = await load();
      if (r && cur && cur.payment_status === "pending" && n++ < 15) timer = setTimeout(tick, 4000);
    };
    tick();
    http.get("/payments/methods").then((r) => { setMethods(r.data); setMethod(r.data.find((m) => m.active)?.id || ""); });
    return () => clearTimeout(timer);
  }, [code, email, load]);

  const pay = async () => {
    setBusy(true);
    try { await payBooking({ code, email, method, onDone: () => { setBusy(false); load(); } }); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader brand={brand} />
      <div className="mx-auto max-w-lg p-10 text-center">
        <p data-testid="booking-error" className="text-red-600">{error}</p>
        <button onClick={() => nav("/find-booking")} className="mt-4 text-[#5392F9] underline">{t("Cari booking")}</button>
      </div>
    </div>
  );
  const canPay = b && b.status !== "cancelled" && b.payment_type !== "pay_at_hotel" && !["paid", "paid_at_channel", "pay_at_hotel"].includes(b.payment_status);
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <BrandHeader brand={brand} />
      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {!b ? <Skeleton className="h-96 rounded-2xl" /> : (
          <div className="fade-up flex flex-col gap-6">
            <Hero b={b} />
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold">{b.property_name}</h2>
              <p className="text-sm text-slate-500">{b.property?.address}</p>
              <div className="mt-4 divide-y divide-slate-100">
                <Row k={t("Tipe kamar")} v={`${(loc(b, "room_name"))} × ${b.rooms}`} />
                <Row k="Rate plan" v={loc(b, "rate_plan_name") || "-"} testid="booking-rate-plan" />
                <Row k={t("Harga kamar")} v={idr(b.base_subtotal ?? b.subtotal)} />
                {(b.promotions || []).map((p) => <Row key={p.id} k={`Promo: ${t(p.label)} (-${p.discount_percent}%)`} v={`-${idr(p.saved)}`} />)}
                {b.tax > 0 && <Row k={t("Pajak & biaya layanan")} v={idr(b.tax)} />}
                <Row k="Check-in" v={`${fmtDate(b.check_in)} (${b.property?.check_in_time})`} />
                <Row k="Check-out" v={`${fmtDate(b.check_out)} (${b.property?.check_out_time})`} />
                <Row k={t("Tamu")} v={`${b.guest.name} · ${t("{n} orang", { n: b.guests })}`} />
                <Row k={t("Status booking")} v={t(STATUS_LABEL[b.status])} testid="booking-status" />
                <Row k={t("Pembayaran")} v={t(PAY_LABEL[b.payment_status] || b.payment_status)} testid="booking-payment-status" />
                <Row k="Total" v={idr(b.grand_total)} testid="booking-total" />
              </div>
              <p className="mt-3 text-xs text-slate-500">{loc(b.property, "cancellation_policy")}</p>
            </section>
            {canPay && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="font-heading text-lg font-bold">{t("Selesaikan pembayaran")}</h2>
                <div className="mt-4"><PaymentMethods methods={methods} value={method} onChange={setMethod} /></div>
                <button data-testid="booking-pay-button" disabled={busy || !method} onClick={pay}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#5392F9] py-3.5 font-heading font-bold text-white transition-colors hover:bg-[#3B7BE6] disabled:bg-slate-300">
                  {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("Bayar {v}", { v: idr(b.grand_total) })}
                </button>
              </section>
            )}
            <Link to={brandHome(brand)} data-testid="booking-home-link" className="text-center text-sm font-medium text-[#5392F9] hover:underline">{t("Kembali ke beranda")}</Link>
          </div>
        )}
      </main>
      <BrandFooter brand={brand} />
    </div>
  );
}
